import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ChevronDown, ChevronUp, Check, X } from 'lucide-react';
import SafeHTML from '../components/SafeHTML';
import { getFaqKey } from '../utils/keys';

const UAEBankAccountPage = () => {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      question: t('baFaq1Q'),
      answer: t('baFaq1A')
    },
    {
      question: t('baFaq2Q'),
      answer: t('baFaq2A')
    },
    {
      question: t('baFaq3Q'),
      answer: t('baFaq3A')
    },
    {
      question: t('baFaq4Q'),
      answer: t('baFaq4A')
    },
    {
      question: t('baFaq5Q'),
      answer: t('baFaq5A')
    },
    {
      question: t('baFaq6Q'),
      answer: t('baFaq6A')
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <section 
        className="relative h-[500px] bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1721448289017-c9cd61093b5d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHxEdWJhaSUyMHNreWxpbmUlMjBtb2Rlcm4lMjBidXNpbmVzcyUyMG9mZmljZXxlbnwwfHx8fDE3NzYzMjgwOTJ8MA&ixlib=rb-4.1.0&q=85)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="max-w-2xl">
            <div className="inline-block bg-yellow-400 text-black px-4 py-2 rounded font-bold mb-4">
              {t('baBadge')}
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('baHeroTitle')}
            </h1>
            <p className="text-xl text-white/90 mb-8">
              {t('baHeroSubtitle')}
            </p>
            <a
              href="https://wa.me/971585862103?text=Hello%2C%20I%20would%20like%20to%20open%20a%20UAE%20bank%20account"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-gradient-to-r from-logo-blue to-logo-cyan text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all"
            >
              {t('baHeroBtn')}
            </a>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        <div className="prose prose-lg max-w-none mb-16">
          <SafeHTML tag="p" className="text-gray-700 text-lg leading-relaxed" html={t('baIntro1')} />
          <SafeHTML tag="p" className="text-gray-700 text-lg leading-relaxed mt-4" html={t('baIntro2')} />
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">{t('baProcessTitle')}</h2>
          
          <div className="space-y-8">
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-logo-blue rounded-full flex items-center justify-center text-white text-2xl font-bold">1</div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('baProcess1Title')}</h3>
                <p className="text-gray-600">{t('baProcess1Desc')}</p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-logo-blue rounded-full flex items-center justify-center text-white text-2xl font-bold">2</div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('baProcess2Title')}</h3>
                <p className="text-gray-600">{t('baProcess2Desc')}</p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-logo-blue rounded-full flex items-center justify-center text-white text-2xl font-bold">3</div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('baProcess3Title')}</h3>
                <p className="text-gray-600">{t('baProcess3Desc')}</p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-logo-blue rounded-full flex items-center justify-center text-white text-2xl font-bold">4</div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('baProcess4Title')}</h3>
                <p className="text-gray-600">{t('baProcess4Desc')}</p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-logo-blue rounded-full flex items-center justify-center text-white text-2xl font-bold">5</div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('baProcess5Title')}</h3>
                <p className="text-gray-600">{t('baProcess5Desc')}</p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-logo-blue rounded-full flex items-center justify-center text-white text-2xl font-bold">6</div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('baProcess6Title')}</h3>
                <p className="text-gray-600">{t('baProcess6Desc')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('baDocumentsTitle')}</h3>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>{t('baDoc1')}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>{t('baDoc2')}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>{t('baDoc3')}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>{t('baDoc4')}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>{t('baDoc5')}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>{t('baDoc6')}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>{t('baDoc7')}</span>
            </li>
          </ul>
        </div>

        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">{t('baFaqTitle')}</h3>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={getFaqKey(faq.question, index)} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-4 text-left bg-white hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-logo-blue flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-logo-blue flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-4 pb-4 bg-gray-50">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">{t('baPackagesTitle')}</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 p-4 text-left font-bold text-gray-900">{t('features')}</th>
                  <th className="border border-gray-300 p-4 text-center font-bold text-gray-900">
                    <div>{t('basic')}</div>
                    <div className="text-sm font-normal text-gray-600">{t('baTableBasicSub')}</div>
                  </th>
                  <th className="border border-gray-300 p-4 text-center font-bold text-gray-900 bg-blue-50">
                    <div>{t('standard')}</div>
                    <div className="text-sm font-normal text-gray-600">{t('baTableStandardSub')}</div>
                  </th>
                  <th className="border border-gray-300 p-4 text-center font-bold text-gray-900">
                    <div>{t('baTableBusinessTitle')}</div>
                    <div className="text-sm font-normal text-gray-600">{t('baTableBusinessSub')}</div>
                  </th>
                  <th className="border border-gray-300 p-4 text-center font-bold text-gray-900">
                    <div>{t('vip')}</div>
                    <div className="text-sm font-normal text-gray-600">{t('baTableVipSub')}</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 p-3 text-gray-700">{t('baFeature1')}</td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center bg-blue-50"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 p-3 text-gray-700">{t('baFeature2')}</td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center bg-blue-50"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-3 text-gray-700">{t('baFeature3')}</td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center bg-blue-50"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 p-3 text-gray-700">{t('baFeature4')}</td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center bg-blue-50"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-3 text-gray-700">{t('baFeature5')}</td>
                  <td className="border border-gray-300 p-3 text-center"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center bg-blue-50"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 p-3 text-gray-700">{t('baFeature6')}</td>
                  <td className="border border-gray-300 p-3 text-center"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center bg-blue-50"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-3 text-gray-700">{t('baFeature7')}</td>
                  <td className="border border-gray-300 p-3 text-center"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center bg-blue-50"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 p-3 text-gray-700">{t('baFeature8')}</td>
                  <td className="border border-gray-300 p-3 text-center"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center bg-blue-50"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-4"></td>
                  <td className="border border-gray-300 p-4 text-center">
                    <a href="https://wa.me/971585862103?text=I%20want%20Basic%20Bank%20Account" target="_blank" rel="noopener noreferrer" className="inline-block bg-gray-200 hover:bg-gray-300 text-gray-900 px-6 py-2 rounded font-semibold transition-colors">{t('saGetInTouch')}</a>
                  </td>
                  <td className="border border-gray-300 p-4 text-center bg-blue-50">
                    <a href="https://wa.me/971585862103?text=I%20want%20Standard%20Bank%20Account" target="_blank" rel="noopener noreferrer" className="inline-block bg-gradient-to-r from-logo-blue to-logo-cyan hover:shadow-lg text-white px-6 py-2 rounded font-semibold transition-all">{t('saGetInTouch')}</a>
                  </td>
                  <td className="border border-gray-300 p-4 text-center">
                    <a href="https://wa.me/971585862103?text=I%20want%20Business%20Bank%20Account" target="_blank" rel="noopener noreferrer" className="inline-block bg-gray-200 hover:bg-gray-300 text-gray-900 px-6 py-2 rounded font-semibold transition-colors">{t('saGetInTouch')}</a>
                  </td>
                  <td className="border border-gray-300 p-4 text-center">
                    <a href="https://wa.me/971585862103?text=I%20want%20VIP%20Bank%20Account" target="_blank" rel="noopener noreferrer" className="inline-block bg-gray-200 hover:bg-gray-300 text-gray-900 px-6 py-2 rounded font-semibold transition-colors">{t('saGetInTouch')}</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>

      <Footer />
    </div>
  );
};

export default UAEBankAccountPage;
